/**
 * What: this is a tracking client context provider and wraps the application
 * Why: provides the tracking client across the application
 * How: on entry we generate the tracking client
 */
// eslint-disable-next-line monorepo/no-internal-import
import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags/src/useIsSoftFeatureToggleActive';
import type {
  SearchNameType,
  SearchTypeTypes,
  TrackingClient,
  TrackingInitialPageContext,
} from '@aboutyou/legacy-tracking-sdk';
import type {InitialPageInfo} from '@aboutyou/legacy-tracking-sdk/src/ay-tracking/providers/gtm/helpers/clientState';
import React, {
  Context,
  FC,
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {getTrackingClient} from '../utils/getTrackingclient';
import {useTrackingPageContext} from './PageTrackingContext';

export type GTMTrackingClient = Context<TrackingClient>;
type shopIds = {shopId: number};
interface GTMTrackingClientProviderProps {
  shopIds: shopIds;
  referrer: string;
  locale: string;
  currency: string;
  search: string;
  shopGender: string | undefined;
}

export type SearchEventProperties = {
  searchTerm: string;
  searchTermCompleted?: string;
  searchName: SearchNameType;
  searchType?: SearchTypeTypes;
};

export const GTMTrackingClient = createContext<{
  client: TrackingClient;
  shouldFireCustomerDataEvent: () => boolean;
  resetCustomerDataEventScheduler: VoidFunction;
  scheduleCustomerDataEvent: VoidFunction;
  fireCustomerDataCallback: VoidFunction;
  getSearchEventParams: () => null | SearchEventProperties;
  updateSearchEventParams: (params: Partial<SearchEventProperties>) => void;
  resetSearchEventParams: VoidFunction;
}>({
  client: {track: () => {}} as unknown as TrackingClient,
  shouldFireCustomerDataEvent: () => false,
  resetCustomerDataEventScheduler: () => {},
  scheduleCustomerDataEvent: () => {},
  fireCustomerDataCallback: () => {},
  getSearchEventParams: () => null,
  updateSearchEventParams: () => {},
  resetSearchEventParams: () => {},
});

export const GTMTrackingClientProvider: FC<
  React.PropsWithChildren<GTMTrackingClientProviderProps>
> = ({children, ...props}) => {
  const {shopIds, referrer, locale, currency, search, shopGender} = props;
  const isTrackOriginClick = useIsSoftFeatureToggleActive('track_origin_click');
  const enableGA4Version = useIsSoftFeatureToggleActive('track_ga4');
  const pageContext = useTrackingPageContext();

  const currentPage: InitialPageInfo = {
    type: 'initial',
    page: {...pageContext} as TrackingInitialPageContext,
    timestamp: Date.now(),
  };

  const gtmClientParams = {
    gtmClientParams: {
      shopIds,
      featureFlags: {
        isTrackOriginClick: !!isTrackOriginClick,
        enableGAVersion: {
          GA4: !!enableGA4Version,
        },
      },
      referrer,
      locale,
      currency,
      search,
      currentPage,
      shopGender,
    },
  };
  const [client] = useState(() => getTrackingClient(gtmClientParams));

  const fireCustomerDataWithNextUpdate = useRef({state: true, cb: noop});
  const scheduleCustomerDataEvent = useCallback((cb: VoidFunction = noop) => {
    fireCustomerDataWithNextUpdate.current = {
      state: true,
      cb,
    };
  }, []);
  const resetCustomerDataEventScheduler = useCallback(() => {
    fireCustomerDataWithNextUpdate.current = {
      state: false,
      cb: noop,
    };
  }, []);
  const shouldFireCustomerDataEvent = useCallback(() => {
    return fireCustomerDataWithNextUpdate.current.state;
  }, []);
  const fireCustomerDataCallback = useCallback(() => {
    return fireCustomerDataWithNextUpdate.current.cb();
  }, []);

  const defaultSearchEventProperties: SearchEventProperties = {
    searchTerm: '',
    searchName: '',
    searchType: undefined,
  };

  const searchEventState = useRef<SearchEventProperties>(
    defaultSearchEventProperties,
  );

  const getSearchEventParams = useCallback(() => {
    return searchEventState.current;
  }, [searchEventState.current]);

  const updateSearchEventParams = useCallback(
    (props: Partial<SearchEventProperties>) => {
      searchEventState.current = {
        ...searchEventState.current,
        ...props,
      };
    },
    [],
  );

  const resetSearchEventParams = useCallback(() => {
    searchEventState.current = defaultSearchEventProperties;
  }, []);

  const state = useMemo(
    () => ({
      client,
      scheduleCustomerDataEvent,
      shouldFireCustomerDataEvent,
      fireCustomerDataCallback,
      resetCustomerDataEventScheduler,
      getSearchEventParams,
      updateSearchEventParams,
      resetSearchEventParams,
    }),
    [
      client,
      scheduleCustomerDataEvent,
      shouldFireCustomerDataEvent,
      fireCustomerDataCallback,
      resetCustomerDataEventScheduler,
      getSearchEventParams,
      updateSearchEventParams,
      resetSearchEventParams,
    ],
  );

  return (
    <GTMTrackingClient.Provider value={state}>
      {children}
    </GTMTrackingClient.Provider>
  );
};

const noop = () => {};
